import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, BarChart3, Sparkles, ArrowRight, Shield, TrendingUp, Award, ChevronRight, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import CardItem from '@/components/CardItem';
import { getCards, getBanks, getCardTypes } from '@/lib/api';
import { useCompare, useFavorites } from '@/lib/hooks';
import { toast } from 'sonner';

const CATEGORY_PILLS = [
  { value: 'all', label: 'All Cards' },
  { value: 'Cashback', label: 'Cash Back' },
  { value: 'Travel', label: 'Travel' },
  { value: 'Rewards', label: 'Rewards' },
  { value: 'Premium', label: 'Premium' },
  { value: 'Islamic', label: 'Islamic' },
];

export default function HomePage() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banks, setBanks] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedBank, setSelectedBank] = useState('all');
  const [sortBy, setSortBy] = useState('none');
  const [sortOrder, setSortOrder] = useState('asc');

  const { compareIds, toggleCompare, isInCompare } = useCompare();
  const { toggleFavorite, isFavorite } = useFavorites();

  const fetchCards = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedType !== 'all') params.card_type = selectedType;
      if (selectedBank !== 'all') params.bank_name = selectedBank;
      if (sortBy !== 'none') { params.sort_by = sortBy; params.sort_order = sortOrder; }
      if (search) params.search = search;
      const res = await getCards(params);
      setCards(res.data);
    } catch { toast.error('Failed to load cards'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCards(); }, [selectedType, selectedBank, sortBy, sortOrder]);
  useEffect(() => { getBanks().then(r => setBanks(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(() => fetchCards(), 400);
    return () => clearTimeout(t);
  }, [search]);

  const handleToggleCompare = (id) => {
    toggleCompare(id);
    if (isInCompare(id)) toast.info('Removed from comparison');
    else toast.success('Added to comparison (max 4)');
  };
  const handleToggleFavorite = (id) => {
    toggleFavorite(id);
    if (isFavorite(id)) toast.info('Removed from favorites');
    else toast.success('Added to favorites');
  };

  const topPicks = cards.slice(0, 3);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section data-testid="hero-section" className="bg-white border-b border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto px-6 pt-10 pb-8">
          <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-[3.5rem] text-[#09090B] tracking-tight leading-[1.1] mb-3">
            Credit Cards
          </h1>
          <p className="text-base md:text-lg text-[#52525B] max-w-2xl mb-8 leading-relaxed">
            Compare top offers by category. Read expert analysis and recommendations.
            Find the right card for your financial goals.
          </p>

          {/* Category pills */}
          <div className="flex flex-wrap gap-2 mb-6">
            {CATEGORY_PILLS.map(cat => (
              <button
                key={cat.value}
                data-testid={`category-${cat.value}`}
                onClick={() => setSelectedType(cat.value)}
                className={`px-4 py-2 rounded-full text-[13px] font-medium transition-all duration-200 border ${
                  selectedType === cat.value
                    ? 'bg-[#002FA7] text-white border-[#002FA7] shadow-sm'
                    : 'bg-white text-[#52525B] border-[#E4E4E7] hover:border-[#002FA7] hover:text-[#002FA7]'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Quick links */}
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/compare" className="flex items-center gap-1.5 text-[#002FA7] hover:underline font-medium">
              <BarChart3 className="w-4 h-4" /> Compare Tool
            </Link>
            <Link to="/recommend" className="flex items-center gap-1.5 text-[#002FA7] hover:underline font-medium">
              <Sparkles className="w-4 h-4" /> AI Card Advisor
            </Link>
          </div>
        </div>
      </section>

      {/* Compare float bar */}
      {compareIds.length > 0 && (
        <div data-testid="compare-bar" className="sticky top-16 z-40 bg-[#002FA7] text-white animate-slide-down">
          <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between">
            <span className="text-[13px] font-medium">
              <BarChart3 className="w-4 h-4 inline mr-1.5" />
              {compareIds.length} card{compareIds.length > 1 ? 's' : ''} selected for comparison
            </span>
            <Link to="/compare">
              <Button data-testid="go-compare-btn" size="sm" variant="secondary" className="h-8 text-[12px] font-semibold rounded-full">
                Compare Now <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
              </Button>
            </Link>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filter row */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-8">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
            <Input
              data-testid="search-input"
              placeholder="Search cards or banks..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10 h-10 bg-white rounded-full border-[#E4E4E7] text-[13px]"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Select value={selectedBank} onValueChange={setSelectedBank}>
              <SelectTrigger data-testid="filter-bank" className="h-10 w-44 rounded-full text-[13px] border-[#E4E4E7]">
                <SelectValue placeholder="All Banks" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Banks</SelectItem>
                {banks.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sortBy !== 'none' ? `${sortBy}-${sortOrder}` : 'none'} onValueChange={v => {
              if (v === 'none') { setSortBy('none'); setSortOrder('asc'); }
              else { const [f, o] = v.split('-'); setSortBy(f); setSortOrder(o); }
            }}>
              <SelectTrigger data-testid="filter-sort" className="h-10 w-48 rounded-full text-[13px] border-[#E4E4E7]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sort: Default</SelectItem>
                <SelectItem value="annual_fee-asc">Fee: Low to High</SelectItem>
                <SelectItem value="annual_fee-desc">Fee: High to Low</SelectItem>
                <SelectItem value="cashback_rate-desc">Cashback: Highest</SelectItem>
                <SelectItem value="min_salary-asc">Salary: Lowest</SelectItem>
              </SelectContent>
            </Select>
            {(selectedBank !== 'all' || search) && (
              <Button variant="ghost" size="sm" onClick={() => { setSelectedBank('all'); setSearch(''); }} className="h-10 rounded-full text-[13px] text-[#71717A]">
                <X className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
            )}
          </div>
        </div>

        {/* Top Picks */}
        {selectedType === 'all' && !search && selectedBank === 'all' && sortBy === 'none' && topPicks.length > 0 && (
          <section className="mb-10" data-testid="top-picks-section">
            <div className="flex items-center gap-2.5 mb-5">
              <Award className="w-5 h-5 text-[#D97706]" />
              <h2 className="font-heading font-bold text-lg text-[#09090B]">Top Picks</h2>
              <span className="text-[12px] text-[#71717A]">Our editor's choice</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {topPicks.map((card, i) => (
                <div key={card.id} className={`animate-fade-in-up stagger-${i + 1}`}>
                  <CardItem
                    card={card}
                    featured
                    onToggleFavorite={handleToggleFavorite}
                    onToggleCompare={handleToggleCompare}
                    isFavorite={isFavorite(card.id)}
                    isCompare={isInCompare(card.id)}
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Results header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <h2 className="font-heading font-bold text-lg text-[#09090B]">
              {selectedType !== 'all' ? `${selectedType} Cards` : 'All Credit Cards'}
            </h2>
            <span className="text-[12px] text-[#71717A] bg-[#F4F4F5] px-2.5 py-0.5 rounded-full">
              {loading ? '...' : cards.length}
            </span>
          </div>
        </div>

        {/* Card grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="border border-[#E4E4E7] rounded-xl overflow-hidden animate-pulse">
                <div className="h-28 bg-[#E4E4E7]" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-[#E4E4E7] rounded w-1/3" />
                  <div className="grid grid-cols-2 gap-3">
                    <div className="h-10 bg-[#E4E4E7] rounded" />
                    <div className="h-10 bg-[#E4E4E7] rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : cards.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-[#E4E4E7] rounded-xl">
            <p className="text-[#52525B] text-lg font-medium mb-1">No cards found</p>
            <p className="text-[#A1A1AA] text-sm">Try adjusting your filters or search terms</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {cards.map((card, i) => (
              <div key={card.id} className={`animate-fade-in-up stagger-${(i % 6) + 1}`}>
                <CardItem
                  card={card}
                  onToggleFavorite={handleToggleFavorite}
                  onToggleCompare={handleToggleCompare}
                  isFavorite={isFavorite(card.id)}
                  isCompare={isInCompare(card.id)}
                />
              </div>
            ))}
          </div>
        )}

        {/* CTA Banner */}
        <div className="mt-12 bg-gradient-to-r from-[#002FA7] to-[#1E40AF] rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center gap-6">
          <div className="flex-1 text-white">
            <h3 className="font-heading font-bold text-xl md:text-2xl mb-2">Not sure which card is right for you?</h3>
            <p className="text-white/80 text-sm md:text-base">Our AI advisor analyzes your spending habits to recommend the perfect card match.</p>
          </div>
          <Link to="/recommend">
            <Button data-testid="cta-ai-advisor" className="bg-white text-[#002FA7] hover:bg-white/90 h-11 px-6 rounded-full font-semibold gap-2">
              <Sparkles className="w-4 h-4" /> Get AI Recommendation
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
